// (c) chaehongee. Confidential and proprietary.

import { useCallback, useEffect, useRef } from 'react'
import { useAppDispatch } from '../../../../store/hooks/hooks'
import { fetchIdentityLoras, selectLora } from '../../../../store/slices/lorasSlice'

interface UseBrushModeIntegrationArgs {
    brushEnabled: boolean
    lorasState: { loading: boolean; items: Array<unknown> }
    setMaskActions: (actions: any) => void
    maskActions: { reset: () => void; exportMask: () => Promise<Blob | null> }
    exportFramedImageWithUpload: () => Promise<{ node: any; commit: any } | null>
    setBrushEnabled: (v: boolean) => void
}

export function useBrushModeIntegration(args: UseBrushModeIntegrationArgs): void {
    const { brushEnabled, lorasState, setMaskActions, maskActions: actions, exportFramedImageWithUpload, setBrushEnabled } = args
    const dispatch = useAppDispatch()

    useEffect(() => {
        if (brushEnabled) {
            if (!lorasState.loading && lorasState.items.length === 0) {
                dispatch(fetchIdentityLoras())
            }
        }
    }, [brushEnabled, dispatch, lorasState.items.length, lorasState.loading])

    const actionsRef = useRef(actions)
    const exportFramedImageWithUploadRef = useRef(exportFramedImageWithUpload)

    useEffect(() => {
        actionsRef.current = actions
    }, [actions])

    useEffect(() => {
        exportFramedImageWithUploadRef.current = exportFramedImageWithUpload
    }, [exportFramedImageWithUpload])

    const exportMask = useCallback(() => actionsRef.current.exportMask(), [])
    const resetMask = useCallback(() => {
        actionsRef.current.reset()
        dispatch(selectLora(null))
    }, [dispatch])
    const setBrushEnabledAction = useCallback((v: boolean) => setBrushEnabled(v), [setBrushEnabled])
    const exportFramed = useCallback(() => exportFramedImageWithUploadRef.current(), [])

    useEffect(() => {
        const next =
            brushEnabled
                ? {
                    exportMask,
                    resetMask,
                    setBrushEnabled: setBrushEnabledAction,
                    exportFramedImageWithUpload: exportFramed,
                }
                : { exportFramedImageWithUpload: exportFramed }

        // Prevent infinite loops when upstream provides unstable function identities:
        // only update state if the action-shape actually changed.
        setMaskActions((prev: any) => (shallowEqual(prev, next) ? prev : next))
    }, [brushEnabled, exportFramed, exportMask, resetMask, setBrushEnabledAction, setMaskActions])
}

export default useBrushModeIntegration

function shallowEqual(a: unknown, b: unknown): boolean {
    if (Object.is(a, b)) return true
    if (!a || !b) return false
    if (typeof a !== 'object' || typeof b !== 'object') return false

    const aObj = a as Record<string, unknown>
    const bObj = b as Record<string, unknown>
    const aKeys = Object.keys(aObj)
    const bKeys = Object.keys(bObj)
    if (aKeys.length !== bKeys.length) return false
    for (const k of aKeys) {
        if (!Object.prototype.hasOwnProperty.call(bObj, k)) return false
        if (!Object.is(aObj[k], bObj[k])) return false
    }
    return true
}

